import { useEffect, useState } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import { ImageOff } from "lucide-react";
import { API, fmtUSD } from "@/lib/api";
import "leaflet/dist/leaflet.css";

function Fit({ lat, lon, r }) {
  const map = useMap();
  useEffect(() => { map.fitBounds(L.latLng(lat, lon).toBounds(r * 1609 * 2), { padding: [10, 10] }); }, [map, lat, lon, r]);
  return null;
}

function Photo({ p, photos }) {
  const [err, setErr] = useState(false);
  if (!photos.google && !photos.mapillary) return <div className="w-[240px] h-[120px] rounded bg-slate-100 text-[10px] text-slate-500 flex flex-col items-center justify-center gap-1 text-center px-3" data-testid="photo-placeholder"><ImageOff className="w-4 h-4" />Street-level photo: connect a Google Street View or Mapillary key</div>;
  if (err) return <div className="w-[240px] h-[60px] rounded bg-slate-100 text-[10px] text-slate-500 flex items-center justify-center">No street-level photo at this location</div>;
  return <img data-testid="comp-photo" src={`${API}/market-comps/photo?lat=${p.lat}&lon=${p.lon}`} onError={() => setErr(true)} alt={p.name} className="w-[240px] h-[135px] object-cover rounded" />;
}

function Details({ p, photos, role, unit }) {
  return (
    <div className="space-y-1.5 text-[11px] min-w-[240px]" data-testid="pin-details">
      <Photo p={p} photos={photos} />
      <div className="font-semibold text-slate-900 text-xs">{p.name}</div>
      <div className="text-slate-500">{role}{p.address ? ` · ${p.address}` : ""}</div>
      <div className="grid grid-cols-2 gap-x-3 text-slate-700">
        <span>Distance</span><span className="num">{p.distance_mi} mi</span>
        {p.year_built && <><span>Year built</span><span className="num">{p.year_built}</span></>}
        {p.levels && <><span>Floors</span><span className="num">{p.levels}</span></>}
        {p.size && <><span>{unit}</span><span className="num">{Math.round(p.size).toLocaleString()}</span></>}
        {p.sale_price && <><span>Last sale</span><span className="num">{fmtUSD(p.sale_price, true)} · {p.sale_date}</span></>}
        {p.occupancy != null && <><span>Occupancy</span><span className="num">{(p.occupancy * 100).toFixed(0)}%</span></>}
      </div>
      {(p.sale_source || p.rent_source) && <div className="text-[10px] text-slate-500">Data: {[p.sale_source && `sale - ${p.sale_source}`, p.rent_source && `rent - ${p.rent_source}`].filter(Boolean).join("; ")}</div>}
    </div>
  );
}

export function CompMap({ study, selected, photos }) {
  const s = study.subject;
  const r = study.params.radius_mi;
  const unit = s.property_type === "multifamily" ? "Units" : "SF";
  return (
    <div className="panel overflow-hidden h-[420px]" data-testid="comp-map">
      <MapContainer center={[s.lat, s.lon]} zoom={14} scrollWheelZoom={false} className="h-full w-full z-0">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Fit lat={s.lat} lon={s.lon} r={r} />
        <Circle center={[s.lat, s.lon]} radius={r * 1609} pathOptions={{ color: "#0284c7", weight: 1, fillOpacity: 0.04 }} />
        {study.map_points.filter((p) => !selected.has(p.osm_id) && p.distance_mi <= r).map((p) => (
          <CircleMarker key={p.osm_id} center={[p.lat, p.lon]} radius={4} pathOptions={{ color: "#94a3b8", weight: 1, fillOpacity: 0.7 }}>
            <Popup minWidth={250}><Details p={p} photos={photos} role="Nearby building" unit={unit} /></Popup>
          </CircleMarker>
        ))}
        {study.map_points.filter((p) => selected.has(p.osm_id)).map((p) => (
          <CircleMarker key={p.osm_id} center={[p.lat, p.lon]} radius={8} pathOptions={{ color: "#0369a1", weight: 2, fillColor: "#0ea5e9", fillOpacity: 0.9 }} eventHandlers={{}}>
            <Popup minWidth={250}><Details p={p} photos={photos} role="Selected comp" unit={unit} /></Popup>
          </CircleMarker>
        ))}
        <CircleMarker center={[s.lat, s.lon]} radius={10} pathOptions={{ color: "#fff", weight: 3, fillColor: "#dc2626", fillOpacity: 1 }}>
          <Popup minWidth={250}><Details p={{ name: s.name || "Subject property", address: s.address, lat: s.lat, lon: s.lon, distance_mi: 0, year_built: s.year_built, size: s.size }} photos={photos} role="Subject" unit={unit} /></Popup>
        </CircleMarker>
      </MapContainer>
    </div>
  );
}
